import { ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import type { DraftState, WizardFile } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import { getSequencingPlatformLabel } from '../../../Util/sequencingPlatform.ts'
import { BOUNDED_TABLE, FoldedBody } from './FoldedRows.tsx'
import { plural } from '../../../Util/plural.ts'
import { RETRY_BUTTON } from './retryStyle.ts'
import { FILE_LIST_TARGET } from '../../../Services/OrderWizard/issues.ts'
import { REJECTION_TEXT } from '../../../Services/OrderWizard/itemPlan.ts'

const formatSize = (bytes: number): string => {
  if (bytes >= 1024 ** 3) {
    return `${(bytes / 1024 ** 3).toFixed(1)} GB`
  }
  if (bytes >= 1024 ** 2) {
    return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** The item a broken file belongs to goes with it; "Re-add" or a drop brings it back. */
const RemoveFile = ({ draft, file }: { draft: DraftState; file: WizardFile }) => {
  const dispatch = useDispatch()
  const group = Object.values(draft.groups).find((candidate) => candidate.forward === file.key || candidate.reverse === file.key)
  if (!group) {
    return null
  }
  return (
    <button
      type="button"
      data-testid="wizard-remove-file"
      className={RETRY_BUTTON}
      onClick={() => dispatch(orderWizardActions.itemsRemoved({ draftId: draft.id, groupKeys: [group.key] }))}
    >
      Remove
    </button>
  )
}

const FileStatus = ({ draft, file }: { draft: DraftState; file: WizardFile }) => {
  const dispatch = useDispatch()
  if (file.rejection) {
    const waitingForMate = file.rejection === REJECTION_TEXT['awaiting-mate'] && !draft.orderId && draft.layoutAnswer === 'add-mates'
    return (
      <span className="text-warning">
        {file.rejection}
        {waitingForMate && (
          <>
            {' · '}
            <button
              type="button"
              className={`${RETRY_BUTTON} px-0`}
              data-testid="wizard-merged-after-all"
              onClick={() => dispatch(orderWizardActions.answerReopened({ draftId: draft.id, kind: 'read-layout' }))}
            >
              These are merged reads after all
            </button>
          </>
        )}
      </span>
    )
  }
  if (file.kind === 'sheet') {
    return <span className="text-slate-600">{draft.sheet?.fileKey === file.key ? 'Sample sheet, read' : 'Sample sheet'}</span>
  }
  const upload = file.upload
  if (!upload) {
    return <span className="text-slate-500">{draft.phase === 'detecting' ? 'Reading…' : 'Waiting'}</span>
  }
  switch (upload.status) {
    case 'pending':
      return <span className="text-slate-500">{draft.orderId ? 'Queued' : 'Waiting for the order'}</span>
    case 'uploading':
      return (
        <span className="flex items-center gap-2">
          <progress className="progress progress-primary w-32" value={Math.round(upload.progress * 100)} max={100} />
          <span className="tabular-nums text-slate-600">{Math.round(upload.progress * 100)}%</span>
          {upload.notice && (
            <span className="text-warning" data-testid="wizard-upload-notice">
              {upload.notice}
            </span>
          )}
        </span>
      )
    case 'uploaded':
      return <span className="text-slate-500">Uploaded</span>
    case 'failed':
      return (
        <span className="flex items-center gap-2 text-error">
          <span className="truncate" title={upload.error}>
            Failed{upload.error ? `: ${upload.error}` : ''}
          </span>
          <button
            type="button"
            data-testid="wizard-retry-upload"
            className={RETRY_BUTTON}
            onClick={() => dispatch(orderWizardActions.uploadRetryRequested({ draftId: draft.id, key: file.key }))}
          >
            Try again
          </button>
          <RemoveFile draft={draft} file={file} />
        </span>
      )
    case 'needs-readd':
      return (
        <span className="flex items-center gap-1 text-warning">
          <ExclamationTriangleIcon className="h-4 w-4" /> Drop it again (or the whole folder) to upload it
          <RemoveFile draft={draft} file={file} />
        </span>
      )
  }
}

/** Uploaded files and the read sheet fold away in a long list; anything still moving or failed stays in view. */
const settled = (draft: DraftState, file: WizardFile) =>
  !file.rejection && (file.upload?.status === 'uploaded' || (file.kind === 'sheet' && draft.sheet?.fileKey === file.key))

/** On the Files blocker: every failed upload tried again at once; one without its file asks for a drop. */
export const RetryFailedUploads = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const failed = Object.values(draft.files).filter((file) => file.upload?.status === 'failed')
  if (failed.length < 2) {
    return null
  }
  return (
    <button
      type="button"
      data-testid="wizard-retry-all-uploads"
      className={`${RETRY_BUTTON} ml-5 px-0`}
      onClick={() => failed.forEach((file) => dispatch(orderWizardActions.uploadRetryRequested({ draftId: draft.id, key: file.key })))}
    >
      Retry all {failed.length} failed
    </button>
  )
}

export const WizardFileList = ({ draft }: { draft: DraftState }) => {
  const files = draft.fileOrder.map((key) => draft.files[key]).filter((file): file is WizardFile => !!file)
  if (files.length === 0) {
    return null
  }
  const renderRow = (file: WizardFile) => (
    <tr key={file.key}>
      <td className="max-w-xs truncate font-mono text-xs" title={file.name}>
        {file.name}
      </td>
      <td className="text-right tabular-nums text-xs">{file.fromServer ? '—' : formatSize(file.size)}</td>
      <td className="text-xs">{file.platform && file.platform !== 'UNKNOWN' ? getSequencingPlatformLabel(file.platform) : '—'}</td>
      <td className="text-xs">
        <FileStatus draft={draft} file={file} />
      </td>
    </tr>
  )
  return (
    <div className={`${BOUNDED_TABLE} transition-shadow`} id={FILE_LIST_TARGET}>
      <table className="table table-sm table-pin-rows" data-testid="wizard-file-list">
        <thead>
          <tr>
            <th>File</th>
            <th className="text-right">Size</th>
            <th>Platform</th>
            <th>Status</th>
          </tr>
        </thead>
        <FoldedBody
          rows={files}
          needsAttention={(file) => !settled(draft, file)}
          renderRow={renderRow}
          colSpan={4}
          okLabel={(count) => `${plural(count, 'file')} uploaded or read`}
        />
      </table>
    </div>
  )
}
